import { AioButtonComponent, AioInputFieldComponent, AioSelectComponent, AioToggleComponent } from '@aio/ui';
import { usPhoneAdapter } from '../../utils/input-field-adapters';
import {
  Component,
  DestroyRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ActionFeedbackService } from '../../../core/services/action-feedback.service';
import { AuthService } from '../../../core/services/auth.service';
import { UserService } from '../../../core/services/user.service';
import { EmailService } from '../../../core/services/email.service';
import { CalendarService, CalendarStatus } from '../../../core/services/calendar.service';
import {
  User,
  UpdateProfileRequest,
  getUserProfileAddressFields,
  mapMeProfileApiDataToUserPartial,
  normalizeOptionalContactNumberForApi,
  normalizeOptionalPhoneNumberTypeForApi,
  phoneTypeToProfileFormValue,
} from '../../../core/models/auth.models';
import {
  Mailbox,
} from '../../../core/models/email.models';
import { SelectOption } from '../../../core/models/select.models';
import { formatDateNumeric } from '../../../shared/utils/date.util';
import {
  formatPhoneNumber,
  normalizeUsPhoneInputDigits,
} from '../../../shared/utils/phone-number.util';
import { extractTenantIdFromRoute } from '../../../shared/utils/route.util';
import { presentActionResultWithActionFeedback } from '../../../shared/utils/action-result-feedback.util';
import { FormValidators } from '../../../shared/utils/form-validators';
import { ActionResult } from '../../../core/models/action-result-toolbar.models';
import { AioBrandedLoaderComponent } from '../aio-branded-loader/aio-branded-loader.component';
import { UploadModalComponent } from '../upload-modal/upload-modal.component';
import { ProfilePictureService } from '../../../core/services/profile-picture.service';
import { NotificationsService } from '../../../core/services/notifications.service';
import { NotificationsPollService } from '../../../core/services/notifications-poll.service';
import { TenantCommsService } from '../../../core/services/tenant-comms.service';
import {
  PROFILE_INTEGRATION_ROWS,
  type ProfileIntegrationKey,
  type ProfileIntegrationRow,
} from '../../../core/models/profile-integration.model';
import {
  isActiveCalendarStatus,
  isGoogleProvider,
  isOutlookProvider,
} from '../../../shared/utils/calendar-provider.util';

@Component({
  selector: 'app-profile-settings-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AioToggleComponent,
    AioBrandedLoaderComponent,
    UploadModalComponent,
    AioInputFieldComponent,
    AioSelectComponent,
    AioButtonComponent,
  ],
  templateUrl: './profile-settings-modal.component.html',
  styleUrl: './profile-settings-modal.component.scss',
})
export class ProfileSettingsModalComponent implements OnInit, OnDestroy {
  protected readonly usPhoneAdapter = usPhoneAdapter;
  @Output() closed = new EventEmitter<void>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly emailService = inject(EmailService);
  private readonly calendarService = inject(CalendarService);
  private readonly router = inject(Router);
  private readonly actionFeedback = inject(ActionFeedbackService);
  private readonly fb = inject(FormBuilder);
  private readonly profilePictureService = inject(ProfilePictureService);
  private readonly notificationsService = inject(NotificationsService);
  private readonly notificationsPollService = inject(NotificationsPollService);
  private readonly tenantComms = inject(TenantCommsService);

  private readonly connectedIntegrationProviders = signal({
    google: false,
    microsoft: false,
  });

  loading = true;
  error: string | null = null;
  user: User | null = null;

  accountEditMode = false;
  saving = false;
  private readonly saveDebounceMs = 1000;
  private lastSaveClick = 0;

  showUploadModal = false;
  resetUploadModal = false;
  selectedProfilePicture: File | null = null;
  private previewObjectUrl: string | null = null;
  /**
   * User chose Remove on a saved photo; UI shows initials until Save or Cancel.
   * On save: PUT `/api/me/profile` then `DELETE /api/me/profile/picture` when there is no new upload.
   */
  pendingRemoveProfilePicture = false;

  readonly selectOverlayOptions = { baseZIndex: 1450 };

  editForm = this.fb.group({
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    roleKey: [{ value: '', disabled: true }],
    street1: [''],
    street2: [''],
    city: [''],
    state: [''],
    zip: [''],
    country: [''],
    countryCode: [''],
    phones: this.fb.array([] as FormGroup[]),
  });

  roleSelectOptions: SelectOption[] = [];

  /** PUT `/api/me/profile` values: `CELL`, `HOME`, `WORK`. */
  readonly phoneTypeOptions: SelectOption[] = [
    { value: 'CELL', label: 'Mobile' },
    { value: 'HOME', label: 'Home' },
    { value: 'WORK', label: 'Work' },
  ];

  /**
   * From GET/PUT `/api/shard/notifications/receiving-preference`.
   * When `true`, the user has disabled receiving notifications (master off).
   */
  isNotificationsDisabled = false;

  /** Toggle model: `true` = user receives notifications (`!isNotificationsDisabled`). */
  receivingNotificationsEnabled = true;

  receivingPreferenceSaving = false;

  /** Linked mailbox account emails (from mailboxes + sync status). */
  gmailAccountEmail = '';
  outlookAccountEmail = '';
  googleMailConnected = false;
  outlookMailConnected = false;

  /** Per-provider calendar connection (from /api/calendar/status). */
  private googleCalendarConnectedFlag = false;
  private outlookCalendarConnectedFlag = false;
  private googleCalendarDisplayLine = '';
  private outlookCalendarDisplayLine = '';

  integrationsBusy = false;

  /** Only mail/calendar rows for the tenant's enabled communications provider. */
  readonly visibleIntegrationRows = computed((): ProfileIntegrationRow[] => {
    const connected = this.connectedIntegrationProviders();
    const state = this.tenantComms.getProviderState();

    if (state.status === 'enabled') {
      if (state.provider === 'google') {
        return PROFILE_INTEGRATION_ROWS.filter(
          (row) => row.key === 'gmail' || row.key === 'google-calendar'
        );
      }
      return PROFILE_INTEGRATION_ROWS.filter(
        (row) => row.key === 'outlook' || row.key === 'outlook-calendar'
      );
    }

    if (state.status === 'none') {
      return [];
    }

    const rows: ProfileIntegrationRow[] = [];
    if (connected.google) {
      rows.push(
        ...PROFILE_INTEGRATION_ROWS.filter(
          (row) => row.key === 'gmail' || row.key === 'google-calendar'
        )
      );
    }
    if (connected.microsoft) {
      rows.push(
        ...PROFILE_INTEGRATION_ROWS.filter(
          (row) => row.key === 'outlook' || row.key === 'outlook-calendar'
        )
      );
    }
    return rows;
  });

  /** Admin guidance when org has explicitly disabled all comms providers. */
  readonly showNoTenantProviderMessage = computed(
    () => this.tenantComms.getProviderState().status === 'none'
  );

  twoFactorEnabled = false;
  /** Immediate PUT when the 2FA toggle changes (view or edit mode). */
  mfaToggleSaving = false;

  ngOnInit(): void {
    this.loadAll();
  }

  ngOnDestroy(): void {
    this.revokePreviewUrl();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.accountEditMode) {
      this.cancelAccountEdit();
      return;
    }
    this.close();
  }

  close(): void {
    if (this.accountEditMode) {
      this.cancelAccountEdit();
    }
    this.closed.emit();
  }

  onOverlayMouseDown(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      if (this.accountEditMode) {
        this.cancelAccountEdit();
      }
      this.close();
    }
  }

  get phonesArray(): FormArray {
    return this.editForm.get('phones') as FormArray;
  }

  loadAll(): void {
    this.loading = true;
    this.error = null;

    const authUser = this.authService.getCurrentUser();
    if (!authUser) {
      this.error = 'You are not authenticated. Please log in.';
      this.loading = false;
      return;
    }

    this.user = authUser;

    forkJoin({
      profile: this.userService.getCurrentUserProfile().pipe(
        catchError((err) => {
          console.error('Profile settings modal — profile API error:', err);
          return of({});
        })
      ),
      integrations: this.loadEmailAndCalendarIntegration(),
      receivingPreference: this.notificationsService.getReceivingPreference(),
    }).subscribe({
      next: ({ profile, receivingPreference }) => {
        const profilePartial = profile as Partial<User>;
        this.user = { ...authUser, ...profilePartial } as User;
        this.tenantComms.applyCalendarProvider(profilePartial.calendarProvider);
        this.twoFactorEnabled = this.user.isMfaRequired === true;
        if (receivingPreference != null) {
          this.isNotificationsDisabled =
            receivingPreference.isNotificationsDisabled === true;
        } else {
          this.isNotificationsDisabled =
            this.notificationsPollService.receivingDisabled();
          this.actionFeedback.showError(
            'Could not load notification preference.'
          );
        }
        this.receivingNotificationsEnabled = !this.isNotificationsDisabled;
        this.notificationsPollService.setReceivingDisabled(this.isNotificationsDisabled);
        this.loading = false;
      },
      error: () => {
        this.user = authUser;
        this.twoFactorEnabled = this.user.isMfaRequired === true;
        this.isNotificationsDisabled =
          this.notificationsPollService.receivingDisabled();
        this.receivingNotificationsEnabled = !this.isNotificationsDisabled;
        this.notificationsPollService.setReceivingDisabled(this.isNotificationsDisabled);
        this.loading = false;
      },
    });
  }

  /**
   * Persists PUT `/api/me/profile` when the MFA toggle changes (no need to enter account edit).
   */
  onTwoFactorToggle(enabled: boolean): void {
    if (!this.user || this.mfaToggleSaving) {
      return;
    }
    const previous = this.user.isMfaRequired === true;
    if (enabled === previous) {
      return;
    }
    this.mfaToggleSaving = true;
    const body: UpdateProfileRequest = {
      firstName: this.user.firstName || '',
      middleName: this.user.middleName || '',
      lastName: this.user.lastName || '',
      contactNumber: normalizeOptionalContactNumberForApi(this.user.contactNumber),
      contactExt: (this.user.contactExt ?? '').toString().trim() || null,
      isMfaRequired: enabled,
      ...getUserProfileAddressFields(this.user),
    };
    this.userService
      .updateUserProfile(body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.mfaToggleSaving = false;
          if (response.success && response.data && this.user) {
            const fromApi = mapMeProfileApiDataToUserPartial(response.data);
            this.user = {
              ...this.user,
              ...fromApi,
              isMfaRequired: enabled,
            } as User;
            this.twoFactorEnabled = enabled;
            this.authService.updateCurrentUser(this.user);
            const msg = (response.message || '').trim();
            this.actionFeedback.showSuccess(msg || 'Sign-in security updated.');
          } else {
            this.twoFactorEnabled = previous;
            this.actionFeedback.showError(
              response.message || 'Could not update two-factor setting.'
            );
          }
        },
        error: (err) => {
          this.mfaToggleSaving = false;
          this.twoFactorEnabled = previous;
          this.actionFeedback.showError(
            err?.error?.message ||
              err?.message ||
              'Could not update two-factor setting.'
          );
        },
      });
  }

  /**
   * Persists PUT `/api/shard/notifications/receiving-preference` when the master toggle changes.
   */
  onReceivingNotificationsEnabledChange(enabled: boolean): void {
    const desiredDisabled = !enabled;
    if (desiredDisabled === this.isNotificationsDisabled) {
      return;
    }
    const rollbackEnabled = !this.isNotificationsDisabled;
    this.receivingPreferenceSaving = true;
    this.notificationsService
      .updateReceivingPreference({ isNotificationsDisabled: desiredDisabled })
      .subscribe({
        next: (res) => {
          this.receivingPreferenceSaving = false;
          if (res.success && res.data != null) {
            this.isNotificationsDisabled =
              res.data.isNotificationsDisabled === true;
            this.receivingNotificationsEnabled =
              !this.isNotificationsDisabled;
            this.notificationsPollService.setReceivingDisabled(
              this.isNotificationsDisabled
            );
            const msg = (res.message || '').trim();
            this.actionFeedback.showSuccess(
              msg || 'Notification preference saved.'
            );
          } else {
            this.receivingNotificationsEnabled = rollbackEnabled;
            this.actionFeedback.showError(
              res.message || 'Could not update notification preference.'
            );
          }
        },
        error: (err) => {
          this.receivingPreferenceSaving = false;
          this.receivingNotificationsEnabled = rollbackEnabled;
          this.actionFeedback.showError(
            err?.error?.message ||
              err?.message ||
              'Could not update notification preference.'
          );
        },
      });
  }

  loadUserProfile(): void {
    this.loadAll();
  }

  enterAccountEdit(): void {
    if (!this.user) {
      return;
    }
    this.revokePreviewUrl();
    this.selectedProfilePicture = null;
    this.pendingRemoveProfilePicture = false;
    this.roleSelectOptions = [
      {
        value: this.getRoleLabel(),
        label: this.getRoleLabel(),
      },
    ];
    this.phonesArray.clear();
    const primary = normalizeUsPhoneInputDigits(this.user.contactNumber || '');
    const phoneType = phoneTypeToProfileFormValue(this.user.phoneNumberType);
    this.phonesArray.push(
      this.createPhoneSlotGroup(
        primary,
        phoneType,
        (this.user.contactExt ?? '').toString().trim()
      )
    );
    this.reconcilePhoneSlotValidators();
    this.editForm.patchValue({
      firstName: this.user.firstName || '',
      lastName: this.user.lastName || '',
      street1: this.user.street1 || '',
      street2: this.user.street2 || '',
      city: this.user.city || '',
      state: this.user.state || '',
      zip: this.user.zip || '',
      country: this.user.country || '',
      countryCode: this.user.countryCode || '',
    });
    const roleCtrl = this.editForm.get('roleKey');
    if (roleCtrl) {
      roleCtrl.enable({ emitEvent: false });
      roleCtrl.setValue(this.getRoleLabel(), { emitEvent: false });
      roleCtrl.disable({ emitEvent: false });
    }
    this.accountEditMode = true;
  }

  cancelAccountEdit(): void {
    if (!this.accountEditMode) {
      return;
    }
    this.accountEditMode = false;
    this.saving = false;
    this.pendingRemoveProfilePicture = false;
    this.showUploadModal = false;
    this.selectedProfilePicture = null;
    this.resetUploadModal = true;
    setTimeout(() => {
      this.resetUploadModal = false;
    }, 0);
    this.revokePreviewUrl();
    this.phonesArray.clear();
    this.editForm.reset();
  }

  onReplaceImageClick(): void {
    this.showUploadModal = true;
  }

  /** True when user can clear a pending upload or mark a saved photo for removal. */
  canRemoveProfilePicture(): boolean {
    return !!(
      this.previewObjectUrl ||
      this.selectedProfilePicture ||
      (this.user?.profilePictureUrl?.trim() && !this.pendingRemoveProfilePicture)
    );
  }

  /**
   * Clears a not-yet-saved image selection, or marks a saved photo for removal (applied on Save).
   */
  onRemoveProfilePicture(): void {
    if (this.saving) {
      return;
    }
    if (this.previewObjectUrl || this.selectedProfilePicture) {
      this.revokePreviewUrl();
      this.selectedProfilePicture = null;
      return;
    }
    const existing = this.user?.profilePictureUrl?.trim();
    if (!existing || this.pendingRemoveProfilePicture) {
      return;
    }
    this.pendingRemoveProfilePicture = true;
  }

  onUploadModalClosed(open: boolean): void {
    this.showUploadModal = open;
  }

  onUploadFileSelected(file: File): void {
    this.selectedProfilePicture = file;
    this.pendingRemoveProfilePicture = false;
    this.revokePreviewUrl();
    this.previewObjectUrl = URL.createObjectURL(file);
  }

  /** Toast when the profile upload modal finishes its success flow (before Done). */
  onProfileImageUploadSucceeded(_fileName: string): void {
    const result: ActionResult = {
      action: 'uploaded',
      count: 1,
      itemLabel: 'image',
      actionPastTense: 'uploaded successfully',
      timestamp: new Date(),
      isCustomMessage: false,
    };
    presentActionResultWithActionFeedback(this.actionFeedback, result, { allowWrap: true });
  }

  addPhoneNumber(): void {
    if (this.phonesArray.length >= 5) {
      return;
    }
    this.phonesArray.push(this.createPhoneSlotGroup('', '', ''));
    this.reconcilePhoneSlotValidators();
  }

  removePhoneSlot(index: number): void {
    if (this.phonesArray.length <= 1) {
      return;
    }
    this.phonesArray.removeAt(index);
    this.reconcilePhoneSlotValidators();
  }

  canRemovePhoneSlot(): boolean {
    return this.phonesArray.length > 1;
  }

  private createPhoneSlotGroup(
    numberValue: string,
    phoneType = '',
    extensionValue = ''
  ): FormGroup {
    return this.fb.group({
      phoneType: [phoneType],
      number: [numberValue, []],
      extension: [extensionValue, []],
    });
  }

  /** Phone type and number are optional; validate format when a number is entered. */
  private reconcilePhoneSlotValidators(): void {
    this.phonesArray.controls.forEach((ctrl) => {
      const g = ctrl as FormGroup;
      const num = g.get('number');
      const typ = g.get('phoneType');
      num?.setValidators([FormValidators.phoneNumber()]);
      typ?.setValidators([]);
      num?.updateValueAndValidity({ emitEvent: false });
      typ?.updateValueAndValidity({ emitEvent: false });
    });
  }

  canAddPhoneNumber(): boolean {
    return this.phonesArray.length < 5;
  }

  onPhoneSlotBlur(index: number): void {
    (this.phonesArray.at(index) as FormGroup).get('number')?.markAsTouched();
  }

  getEditAvatarImageSrc(): string {
    if (this.previewObjectUrl) {
      return this.previewObjectUrl;
    }
    if (this.pendingRemoveProfilePicture) {
      return '';
    }
    return this.user?.profilePictureUrl?.trim() || '';
  }

  editAvatarShowsImage(): boolean {
    return !!this.getEditAvatarImageSrc();
  }

  /** “Upload Image” when there is no photo/preview; “Replace Image” when one is shown. */
  getProfileImageActionLabel(): string {
    return this.editAvatarShowsImage() ? 'Replace Image' : 'Upload Image';
  }

  onSaveAccount(): void {
    const now = Date.now();
    if (now - this.lastSaveClick < this.saveDebounceMs) {
      return;
    }
    this.lastSaveClick = now;

    if (this.saving || !this.user) {
      return;
    }

    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      this.actionFeedback.showError(this.buildAccountSaveValidationSummary());
      return;
    }

    const firstSlot = this.phonesArray.at(0) as FormGroup;
    const phoneDigits = (firstSlot.get('number')?.value || '').replace(
      /\D/g,
      ''
    );

    this.saving = true;

    const profileData: UpdateProfileRequest = {
      firstName: this.editForm.get('firstName')?.value || '',
      middleName: '',
      lastName: this.editForm.get('lastName')?.value || '',
      contactNumber: normalizeOptionalContactNumberForApi(phoneDigits),
      contactExt:
        (firstSlot.get('extension')?.value ?? '').toString().trim() || null,
      isMfaRequired: this.twoFactorEnabled,
      phoneNumberType: normalizeOptionalPhoneNumberTypeForApi(
        firstSlot.get('phoneType')?.value
      ),
      ...this.getAddressFieldsFromEditForm(),
    };

    const finishSave = () => {
      if (this.selectedProfilePicture) {
        this.profilePictureService
          .convertFileToProfilePictureData(this.selectedProfilePicture)
          .subscribe({
            next: (data) => {
              profileData.profilePicture = data;
              this.submitProfileUpdate(profileData);
            },
            error: () => {
              this.saving = false;
              this.actionFeedback.showError('Could not process profile image.');
            },
          });
      } else {
        this.submitProfileUpdate(profileData);
      }
    };

    finishSave();
  }

  /** User-facing summary when Save is blocked by reactive-form validation. */
  private buildAccountSaveValidationSummary(): string {
    const labels: string[] = [];
    if (this.editForm.get('firstName')?.invalid) {
      labels.push('first name');
    }
    if (this.editForm.get('lastName')?.invalid) {
      labels.push('last name');
    }
    const firstSlot = this.phonesArray.at(0) as FormGroup | undefined;
    const num = firstSlot?.get('number');
    if (num?.invalid) {
      labels.push('a valid 10-digit phone number');
    }
    if (labels.length === 0) {
      return 'Please fix the highlighted fields before saving your profile.';
    }
    if (labels.length === 1) {
      const only = labels[0];
      if (only === 'a valid 10-digit phone number') {
        return 'Please enter a valid 10-digit primary phone number before saving your profile.';
      }
      if (only === 'first name' || only === 'last name') {
        return `Please complete your ${only} before saving your profile.`;
      }
      return `Please complete ${only} before saving your profile.`;
    }
    const last = labels[labels.length - 1];
    const rest = labels.slice(0, -1).join(', ');
    return `Please complete ${rest}, and ${last} before saving your profile.`;
  }

  private getAddressFieldsFromEditForm(): Pick<
    UpdateProfileRequest,
    'street1' | 'street2' | 'city' | 'state' | 'zip' | 'countryCode' | 'country'
  > {
    const raw = this.editForm.getRawValue();
    const trim = (value: unknown) => (value == null ? '' : String(value)).trim();
    return {
      street1: trim(raw.street1),
      street2: trim(raw.street2),
      city: trim(raw.city),
      state: trim(raw.state),
      zip: trim(raw.zip),
      countryCode: trim(raw.countryCode),
      country: trim(raw.country),
    };
  }

  private submitProfileUpdate(profileData: UpdateProfileRequest): void {
    /** PUT first; DELETE clears stored picture when user removed it and did not upload a replacement. */
    const removePictureAfterPut =
      this.pendingRemoveProfilePicture && !profileData.profilePicture;

    const put$ = this.userService.updateUserProfile(profileData);
    const request$ = removePictureAfterPut
      ? put$.pipe(
          switchMap((putResponse) =>
            this.userService
              .deleteUserProfilePicture()
              .pipe(map(() => putResponse))
          )
        )
      : put$;

    request$.subscribe({
      next: (response) => {
        this.saving = false;
        if (response.success && response.data && this.user) {
          const fromApi = mapMeProfileApiDataToUserPartial(response.data);
          const picFromApi = response.data.profilePictureUrl;
          let pic =
            picFromApi !== undefined && picFromApi !== null
              ? String(picFromApi)
              : this.user.profilePictureUrl || '';
          if (removePictureAfterPut) {
            pic = '';
          }
          this.user = {
            ...this.user,
            ...fromApi,
            street1: profileData.street1,
            street2: profileData.street2,
            city: profileData.city,
            state: profileData.state,
            zip: profileData.zip,
            countryCode: profileData.countryCode,
            country: profileData.country,
            phoneNumberType: profileData.phoneNumberType,
            contactExt: profileData.contactExt,
            profilePictureUrl: pic,
          } as User;
          this.twoFactorEnabled = this.user.isMfaRequired === true;
          this.authService.updateCurrentUser(this.user);
          this.revokePreviewUrl();
          this.selectedProfilePicture = null;
          this.pendingRemoveProfilePicture = false;
          this.accountEditMode = false;
          this.actionFeedback.showSuccess('Profile updated.');
        } else {
          this.actionFeedback.showError(
            response.message || 'Failed to update profile.'
          );
        }
      },
      error: (err) => {
        this.saving = false;
        this.actionFeedback.showError(
          err?.error?.message || err?.message || 'Failed to update profile.'
        );
      },
    });
  }

  private revokePreviewUrl(): void {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
  }

  private loadEmailAndCalendarIntegration(): Observable<void> {
    return this.emailService.getMailboxes().pipe(
      switchMap((mr) => {
        const list =
          mr.success && mr.data ? mr.data.filter((m) => m.isValid !== false) : [];
        const gmailBox = list.find((m) => isGoogleProvider(m.provider)) ?? null;
        const outlookBox = list.find((m) => isOutlookProvider(m.provider)) ?? null;

        return forkJoin({
          gmailMail: gmailBox ? this.resolveMailConnected(gmailBox) : of(false),
          outlookMail: outlookBox ? this.resolveMailConnected(outlookBox) : of(false),
          calendars: this.calendarService
            .getStatus()
            .pipe(catchError(() => of([] as CalendarStatus[]))),
          gmailBox: of(gmailBox),
          outlookBox: of(outlookBox),
        });
      }),
      catchError(() =>
        forkJoin({
          gmailMail: of(false),
          outlookMail: of(false),
          calendars: this.calendarService
            .getStatus()
            .pipe(catchError(() => of([] as CalendarStatus[]))),
          gmailBox: of(null as Mailbox | null),
          outlookBox: of(null as Mailbox | null),
        })
      ),
      map(({ gmailMail, outlookMail, calendars, gmailBox, outlookBox }) => {
        this.applyConnectedAccountsState({
          gmailMail,
          outlookMail,
          calendars,
          gmailBox,
          outlookBox,
        });
      }),
      map(() => void 0)
    );
  }

  private applyConnectedAccountsState(result: {
    gmailMail: boolean;
    outlookMail: boolean;
    calendars: CalendarStatus[];
    gmailBox: Mailbox | null;
    outlookBox: Mailbox | null;
  }): void {
    const { gmailMail, outlookMail, calendars, gmailBox, outlookBox } = result;

    this.googleMailConnected = gmailMail;
    this.outlookMailConnected = outlookMail;
    this.gmailAccountEmail =
      gmailMail && gmailBox?.accountEmail ? gmailBox.accountEmail.trim() : '';
    this.outlookAccountEmail =
      outlookMail && outlookBox?.accountEmail ? outlookBox.accountEmail.trim() : '';

    const gmailCal = calendars.find(
      (s) => isGoogleProvider(s.provider) && isActiveCalendarStatus(s)
    );
    const outlookCal = calendars.find(
      (s) => isOutlookProvider(s.provider) && isActiveCalendarStatus(s)
    );

    this.googleCalendarConnectedFlag = !!gmailCal;
    this.outlookCalendarConnectedFlag = !!outlookCal;

    this.connectedIntegrationProviders.set({
      google: gmailMail || !!gmailCal,
      microsoft: outlookMail || !!outlookCal,
    });

    this.googleCalendarDisplayLine = gmailCal
      ? gmailCal.providerDisplayName?.trim() ||
        gmailCal.providerUserId?.trim() ||
        this.gmailAccountEmail ||
        'Connected'
      : '';

    this.outlookCalendarDisplayLine = outlookCal
      ? outlookCal.providerDisplayName?.trim() ||
        outlookCal.providerUserId?.trim() ||
        this.outlookAccountEmail ||
        'Connected'
      : '';

  }

  private resolveMailConnected(mailbox: Mailbox): Observable<boolean> {
    if (!mailbox.mailboxId || mailbox.isValid === false) {
      return of(false);
    }

    return this.emailService.getSyncStatus(mailbox.mailboxId).pipe(
      switchMap((syncResp) => {
        if (!syncResp.success || !syncResp.data) {
          return of(false);
        }

        const d = syncResp.data;
        if (d.lastErrorMsg?.toLowerCase().includes('oauth token refresh failed')) {
          return of(false);
        }

        const activeWebhook = d.activeMode === 'webhook';
        const validFlag = d.isValid !== false;

        if (activeWebhook && validFlag) {
          return of(true);
        }

        return this.emailService.startSync(mailbox.mailboxId!).pipe(
          map(
            (start) =>
              !!(
                start.success &&
                start.data &&
                (start.data.status === 'Accepted' || start.data.syncState != null)
              )
          ),
          catchError(() => of(false))
        );
      }),
      catchError(() => of(false))
    );
  }

  /** Active calendar row is Google (from /api/calendar/status). */
  googleCalendarConnected(): boolean {
    return this.googleCalendarConnectedFlag;
  }

  /** Active calendar row is Outlook (from /api/calendar/status). */
  outlookCalendarConnected(): boolean {
    return this.outlookCalendarConnectedFlag;
  }

  private buildReturnDisplayUrl(): string {
    const urlParts = this.router.url.split('/').filter((part) => part.length > 0);
    const tenantId = extractTenantIdFromRoute(this.router);
    if (tenantId && urlParts.length > 0 && urlParts[0] === tenantId) {
      return urlParts.slice(1).join('/');
    }
    return urlParts.join('/');
  }

  private assertIntegrationAllowed(key: ProfileIntegrationKey): boolean {
    if (!this.tenantComms.isIntegrationAllowedForTenant(key)) {
      const providerLabel =
        key === 'gmail' || key === 'google-calendar' ? 'Google Workspace' : 'Microsoft 365';
      this.actionFeedback.showError(
        `${providerLabel} is not enabled for your organization. Ask an admin to enable it under Admin → Integrations.`
      );
      return false;
    }
    return true;
  }

  connectIntegration(key: ProfileIntegrationKey): void {
    if (!this.assertIntegrationAllowed(key)) {
      return;
    }
    if (key === 'gmail') {
      if (this.outlookMailConnected) {
        this.actionFeedback.showError(
          'You can only have one email linked at a time. Please disconnect the other provider first.'
        );
        return;
      }
      this.startConnectEmail('gmail', 'Could not start Gmail connection.', 'Could not connect Gmail.');
      return;
    }
    if (key === 'outlook') {
      if (this.googleMailConnected) {
        this.actionFeedback.showError(
          'You can only have one email linked at a time. Please disconnect the other provider first.'
        );
        return;
      }
      this.startConnectEmail(
        'outlook',
        'Could not start Outlook connection.',
        'Could not connect Outlook.'
      );
      return;
    }
    if (key === 'google-calendar') {
      this.startCalendarOAuth('Google');
      return;
    }
    this.startCalendarOAuth('Outlook');
  }

  disconnectIntegration(key: ProfileIntegrationKey): void {
    if (key === 'gmail') {
      this.onDisconnectGmail();
      return;
    }
    if (key === 'outlook') {
      this.onDisconnectOutlookMail();
      return;
    }
    if (key === 'google-calendar') {
      this.onDisconnectGoogleCalendar();
      return;
    }
    this.onDisconnectOutlookCalendar();
  }

  isIntegrationRowConnected(key: ProfileIntegrationKey): boolean {
    switch (key) {
      case 'gmail':
        return this.googleMailConnected;
      case 'google-calendar':
        return this.googleCalendarConnected();
      case 'outlook':
        return this.outlookMailConnected;
      case 'outlook-calendar':
        return this.outlookCalendarConnected();
    }
  }

  integrationDisplayLine(key: ProfileIntegrationKey): string {
    switch (key) {
      case 'gmail':
        return this.getGoogleMailDisplayLine();
      case 'google-calendar':
        return this.getGoogleCalendarDisplayLine();
      case 'outlook':
        return this.getOutlookMailDisplayLine();
      case 'outlook-calendar':
        return this.getOutlookCalendarDisplayLine();
    }
  }

  integrationHelpText(key: ProfileIntegrationKey): string {
    const connected = this.isIntegrationRowConnected(key);
    switch (key) {
      case 'gmail':
        return connected
          ? "This Google account is connected to your Google inbox. Disconnecting will remove AIO's access to send and receive emails from this account."
          : 'Connect your Google account to send and receive email in AIO.';
      case 'google-calendar':
        return connected
          ? "This Google account is connected to your Google calendar. Disconnecting will remove AIO's access to sync calendar events for this account."
          : 'Connect Google Calendar to sync events with AIO.';
      case 'outlook':
        return connected
          ? "This Microsoft account is connected to your Outlook inbox. Disconnecting will remove AIO's access to send and receive email from this account."
          : 'Connect your Microsoft work or personal account to use Outlook in AIO.';
      case 'outlook-calendar':
        return connected
          ? 'Your Outlook calendar is connected. Disconnecting will stop syncing those events with AIO.'
          : 'Connect Outlook Calendar to sync events with AIO.';
    }
  }

  private startConnectEmail(
    provider: string,
    startFailedMsg: string,
    genericErrMsg: string
  ): void {
    if (this.integrationsBusy) {
      return;
    }
    this.integrationsBusy = true;
    const returnDisplayUrl = this.buildReturnDisplayUrl();
    this.emailService.connectEmailAccount(provider, returnDisplayUrl).subscribe({
      next: (response) => {
        this.integrationsBusy = false;
        if (response.success && response.data?.oAuthUrl) {
          window.location.href = response.data.oAuthUrl;
          return;
        }
        this.actionFeedback.showError(response.message || startFailedMsg);
      },
      error: (err) => {
        this.integrationsBusy = false;
        this.actionFeedback.showError(
          err?.error?.message || err?.message || genericErrMsg
        );
      },
    });
  }

  onDisconnectGmail(): void {
    if (!this.googleMailConnected) {
      return;
    }
    this.disconnectMailIntegrationWithSuccessMessage(
      'Google',
      'Gmail and Google Calendar disconnected.'
    );
  }

  onDisconnectOutlookMail(): void {
    if (!this.outlookMailConnected) {
      return;
    }
    this.disconnectMailIntegrationWithSuccessMessage(
      'Outlook',
      'Outlook mail and calendar disconnected.'
    );
  }

  /**
   * POST /api/calendar/disconnect/all — removes email + calendar for the provider (same as admin integrations).
   */
  private disconnectMailIntegrationWithSuccessMessage(
    provider: 'Google' | 'Outlook',
    fallbackSuccessMsg: string
  ): void {
    if (this.integrationsBusy) {
      return;
    }
    this.integrationsBusy = true;
    this.calendarService.disconnectAll(provider).subscribe({
      next: (res) => {
        this.integrationsBusy = false;
        const msg = res.message?.trim();
        this.actionFeedback.showSuccess(msg || fallbackSuccessMsg);
        this.calendarService.notifyConnectionChanged();
        this.refreshIntegrationsOnly();
      },
      error: (err) => {
        this.integrationsBusy = false;
        this.actionFeedback.showError(
          err?.error?.message ||
            err?.message ||
            'Could not disconnect email integration.'
        );
      },
    });
  }

  private startCalendarOAuth(provider: 'Google' | 'Outlook'): void {
    if (this.integrationsBusy) {
      return;
    }
    this.integrationsBusy = true;
    const returnDisplayUrl = this.buildReturnDisplayUrl();
    let redirectUri: string;
    switch (provider) {
      case 'Google':
        redirectUri = 'api/google/calendar/oauth-redirect';
        break;
      case 'Outlook':
        redirectUri = 'api/outlook/calendar/oauth-redirect';
        break;
    }

    this.calendarService
      .connectCalendar(provider, returnDisplayUrl, redirectUri)
      .subscribe({
        next: (response) => {
          this.integrationsBusy = false;
          const authUrl =
            response.data?.authorizeUrl || response.data?.oAuthUrl;
          if (response.success && authUrl) {
            window.location.href = authUrl;
            return;
          }
          this.actionFeedback.showError(
            response.message || 'Could not start calendar connection.'
          );
        },
        error: (err) => {
          this.integrationsBusy = false;
          this.actionFeedback.showError(
            err?.error?.message ||
              err?.message ||
              'Could not connect calendar.'
          );
        },
      });
  }

  onDisconnectGoogleCalendar(): void {
    this.disconnectCalendarProvider('Google', 'Google Calendar disconnected.');
  }

  onDisconnectOutlookCalendar(): void {
    this.disconnectCalendarProvider('Outlook', 'Outlook Calendar disconnected.');
  }

  private disconnectCalendarProvider(
    provider: string,
    successMsg: string
  ): void {
    if (this.integrationsBusy) {
      return;
    }
    this.integrationsBusy = true;
    this.calendarService.disconnectCalendar(provider).subscribe({
      next: () => {
        this.integrationsBusy = false;
        this.actionFeedback.showSuccess(successMsg);
        this.calendarService.notifyConnectionChanged();
        this.refreshIntegrationsOnly();
      },
      error: (err) => {
        this.integrationsBusy = false;
        this.actionFeedback.showError(
          err?.error?.message || err?.message || 'Could not disconnect calendar.'
        );
      },
    });
  }

  private refreshIntegrationsOnly(): void {
    this.integrationsBusy = true;
    this.loadEmailAndCalendarIntegration().subscribe({
      next: () => {
        this.integrationsBusy = false;
      },
      error: () => {
        this.integrationsBusy = false;
      },
    });
  }

  getFullName(): string {
    if (!this.user) return '';
    const first = this.user.firstName || '';
    const last = this.user.lastName || '';
    return `${first} ${last}`.trim() || '—';
  }

  getJoinedDate(): string {
    if (!this.user?.dateCreated) return '—';
    return formatDateNumeric(this.user.dateCreated);
  }

  getRoleLabel(): string {
    if (!this.user) return '—';
    if (this.user.roleName?.trim()) {
      return this.mapRoleLabel(this.user.roleName.trim());
    }
    if (this.user.globalRoles?.length) {
      const r = this.user.globalRoles[0];
      return this.mapRoleLabel(
        typeof r === 'string' ? r : (r as { name?: string }).name || 'User'
      );
    }
    return 'User';
  }

  private mapRoleLabel(role: string): string {
    switch (role) {
      case 'admin':
      case 'Admin':
        return 'Administrator';
      case 'manager':
      case 'Manager':
        return 'Manager';
      case 'user':
      case 'User':
        return 'User';
      default:
        return role || 'User';
    }
  }

  getPhoneLines(): string[] {
    if (!this.user) return [];
    const main = this.formatPhoneDisplay(this.user.contactNumber || '');
    const ext = (this.user.contactExt || '').trim();
    if (main && ext) {
      return [`${main} Ext. ${ext}`];
    }
    if (main) {
      return [main];
    }
    return [];
  }

  private formatPhoneDisplay(raw: string): string {
    if (!raw) return '';
    return formatPhoneNumber(raw);
  }

  getAddressDisplay(): string {
    if (!this.user) {
      return '—';
    }
    const cityState = [this.user.city, this.user.state]
      .filter((part) => part && String(part).trim())
      .join(', ');
    const cityStateZip = [cityState, this.user.zip]
      .filter((part) => part && String(part).trim())
      .join(' ');
    const parts = [
      this.user.street1,
      this.user.street2,
      cityStateZip,
      this.user.country,
    ].filter((part) => part && String(part).trim());
    return parts.length ? parts.join(', ') : '—';
  }

  getUserInitials(): string {
    if (!this.user) return '';
    const f = this.user.firstName || '';
    const l = this.user.lastName || '';
    return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
  }

  hasProfilePicture(): boolean {
    return !!(
      this.user?.profilePictureUrl &&
      this.user.profilePictureUrl.trim() !== ''
    );
  }

  getGoogleMailDisplayLine(): string {
    if (this.googleMailConnected && this.gmailAccountEmail) {
      return this.gmailAccountEmail;
    }
    return '—';
  }

  getOutlookMailDisplayLine(): string {
    if (this.outlookMailConnected && this.outlookAccountEmail) {
      return this.outlookAccountEmail;
    }
    return '—';
  }

  getGoogleCalendarDisplayLine(): string {
    if (this.googleCalendarConnected() && this.googleCalendarDisplayLine) {
      return this.googleCalendarDisplayLine;
    }
    return '—';
  }

  getOutlookCalendarDisplayLine(): string {
    if (this.outlookCalendarConnected() && this.outlookCalendarDisplayLine) {
      return this.outlookCalendarDisplayLine;
    }
    return '—';
  }

  accountHeaderTitle(): string {
    return 'Account';
  }

  accountHeaderSubtitle(): string {
    return 'Manage your profile, connected accounts, and sign-in security.';
  }

  phoneFieldError(index: number): boolean {
    const g = this.phonesArray.at(index) as FormGroup;
    const c = g.get('number');
    return !!(c && c.invalid && c.touched);
  }
}
